import React from "react";
import API from "@beardeddevops/react.api";
import Types from "constants/Types";
import Framework from "@beardeddevops/react.framework";
import ReactTable from "react-table";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Checkbox from "@material-ui/core/Checkbox";
import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import LeftArrowIcon from "@material-ui/icons/ChevronLeft";
import RightArrowIcon from "@material-ui/icons/ChevronRight";
import {Modal, ModalBody} from "reactstrap";
import Select from "react-select";
import moment from "moment";
import {Objects} from "../index";
import {buildFeWeeklyAuditColumns} from "../../utilities/auditWorkOrders/columns";
import {getTicketFinancial} from "../../utilities/auditWorkOrders/financialDisplay";
import {buildPayFormula, DEFAULT_COMMISSION} from "../../utilities/auditWorkOrders/payFormula";
import {
	calculateDefaultEstimatedDays,
	calculateSuggestedFuelCredits,
	calculateWeeklySummary,
	shouldSuggestBonus,
} from "../../utilities/auditWorkOrders/summaryCalculations";

const FE_WEEKLY_AUDIT_BASE_URL = "/financial/fe_weekly_audit";
const WEEK_TICKET_LIMIT = 200;

export default class FieldEngineerWeeklyAudit extends Framework.Components.Collection {
	constructor(props) {
		const model = new API.BuildClass(Types.Tickets._name, API.RequestType.READ_ALL);
		super(props, model);
		this._loadRequestId = 0;
		this._reviewsCache = {};
		this.columns = buildFeWeeklyAuditColumns({
			onEditFinancial: (ticket) => this.openFinancialModal(ticket),
			onToggleValid: (ticket, financial) => this.toggleFinancialApproved(ticket, financial),
		});
		this.state = {
			...this.state,
			filterDate: moment().day(0).format("YYYY-MM-DD"),
			field_engineer: null,
			field_engineers: null,
			fieldEngineerOptions: [],
			basePay: 0,
			feWorkOrderAmount: 4,
			commission: DEFAULT_COMMISSION,
			feedbackCount: 0,
			travelCount: 0,
			estimatedDays: 0,
			bonus: false,
			estimatedDaysManual: false,
			bonusManual: false,
			travelCountManual: false,
			summary: null,
			data: [],
			loading: false,
			showFinancialModal: false,
			selectedTicketPkey: null,
			selectedFinancialPkey: null,
			showWeekPicker: false,
		};
	}

	componentDidMount() {
		this.fetchFieldEngineers();
	}

	buildFieldEngineerOptions = (users) => {
		/** @type {Record<string, {label: string, ranking: number, options: object[]}>} */
		const groups = {};

		users.forEach((user) => {
			const regionName = user.regions?.name ?? "No Region";
			const regionRanking = user.regions?.ranking ?? 100;

			if (!groups[regionName]) {
				groups[regionName] = {label: regionName, ranking: regionRanking, options: []};
			}

			groups[regionName].options.push({
				value: user.pkey,
				label: `${user.name_first} ${user.name_last}${user.active ? "" : " (Inactive)"}`,
				original: user,
			});
		});

		return Object.values(groups)
			.sort((a, b) => a.ranking - b.ranking)
			.map(({label, options}) => ({
				label,
				options: options.sort((a, b) => a.label.localeCompare(b.label)),
			}));
	};

	fetchFieldEngineers = async () => {
		const model = new API.BuildClass(Types.Users._name, API.RequestType.READ);
		model.limit = 5000;
		model.sort = "ranking";
		model.filter = {inactive: true};

		try {
			const result = await model.submit(null, "GET_FIELD_ENGINEERS");
			let users = result?.items ?? [];
			if (!users.length && result) {
				const parsed = Object.keys(result).map((key) => result[key]);
				users = parsed[3] ?? [];
			}
			this.setState({fieldEngineerOptions: this.buildFieldEngineerOptions(users)});
		} catch (error) {
			console.log(error, "error");
		}
	};

	getSelectedFieldEngineerOption = () => {
		const {field_engineer, field_engineers, fieldEngineerOptions} = this.state;
		if (!field_engineer) return null;

		if (field_engineers?.pkey) {
			return {
				value: field_engineers.pkey,
				label: `${field_engineers.name_first} ${field_engineers.name_last}`,
				original: field_engineers,
			};
		}

		for (const group of fieldEngineerOptions) {
			const match = group.options.find((option) => option.value === field_engineer);
			if (match) return match;
		}

		return {value: field_engineer, label: String(field_engineer)};
	};

	handleFieldEngineerChange = (option) => {
		if (!option) {
			this.handleSelectPropertyUpdate(null, null, null, null);
			return;
		}
		this.handleSelectPropertyUpdate("field_engineer", option.value, null, option.original);
	};

	getWeekKey = (filterDate) => moment(filterDate).format("YYYY-MM-DD");

	getFeedbackCountForWeek = async (fieldEngineer, filterDate) => {
		const weekKey = this.getWeekKey(filterDate);
		if (!this._reviewsCache[weekKey]) {
			const model = new API.BuildClass(Types.Engineers_Reviews._name, API.RequestType.READ_ALL);
			model.filter = {filterDate: weekKey};
			const response = await model.submit(null, "GET_ALL");
			this._reviewsCache[weekKey] = response?.items ?? [];
		}
		const row = this._reviewsCache[weekKey].find((item) => item.engineer === fieldEngineer);
		return row?.total_count ?? 0;
	};

	fetchTicketsForWeek = async (fieldEngineer, filterDate) => {
		const startDate = moment(filterDate).format("YYYY-MM-DD 00:00:00");
		const endDate = moment(filterDate).add(6, "days").format("YYYY-MM-DD 23:59:59");
		const model = new API.BuildClass(Types.Tickets._name, API.RequestType.READ_ALL);
		model.limit = WEEK_TICKET_LIMIT;
		model.page = 0;
		model.filter = {
			field_engineer: fieldEngineer,
			date: [{start: startDate, end: endDate}],
		};
		return await model.submit(null, API.RequestType.READ_ALL);
	};

	fetchEngineerPaySettings = async (pkey) => {
		const model = new API.BuildClass(Types.Users._name, API.RequestType.READ);
		model.object.pkey = pkey;
		return await model.submit(null, "GET_FE_WEEKLY_AUDIT_PAY");
	};

	loadWeekData = async () => {
		const {
			field_engineer,
			filterDate,
			feWorkOrderAmount,
			estimatedDaysManual,
			bonusManual,
			travelCountManual,
		} = this.state;
		if (!field_engineer) {
			this.setState({data: [], loading: false, summary: null});
			return;
		}

		const requestId = ++this._loadRequestId;
		this.setState({loading: true});

		try {
			const [ticketResult, feedbackCount] = await Promise.all([
				this.fetchTicketsForWeek(field_engineer, filterDate),
				this.getFeedbackCountForWeek(field_engineer, filterDate),
			]);

			if (requestId !== this._loadRequestId) return;

			const data = ticketResult?.items ?? [];
			const summary = calculateWeeklySummary(data);
			const updates = {
				data,
				loading: false,
				summary,
				feedbackCount,
			};

			if (!estimatedDaysManual) {
				updates.estimatedDays = calculateDefaultEstimatedDays(
					summary.billedCount,
					feWorkOrderAmount
				);
			}
			if (!bonusManual) {
				updates.bonus = shouldSuggestBonus(summary.avgBilled, summary.billedCount);
			}
			if (!travelCountManual) {
				updates.travelCount = calculateSuggestedFuelCredits(data);
			}

			this.setState(updates);
		} catch (error) {
			if (requestId !== this._loadRequestId) return;
			console.log(error, "error");
			this.setState({loading: false, data: [], summary: null, feedbackCount: 0});
		}
	};

	handleSelectPropertyUpdate = async (property, value, linked, object) => {
		if (!value) {
			await this.setState({
				field_engineer: null,
				field_engineers: null,
				basePay: 0,
				feWorkOrderAmount: 4,
				commission: DEFAULT_COMMISSION,
				travelCount: 0,
				estimatedDays: 0,
				bonus: false,
				feedbackCount: 0,
				estimatedDaysManual: false,
				bonusManual: false,
				travelCountManual: false,
				summary: null,
				data: [],
			});
			return;
		}

		const feData = object;
		let paySettings = {base_pay: 0, fe_work_order_amount: 4, commission: DEFAULT_COMMISSION};
		try {
			paySettings = (await this.fetchEngineerPaySettings(value)) ?? paySettings;
		} catch (error) {
			console.log(error, "error");
		}

		await this.setState(
			{
				field_engineer: value,
				field_engineers: feData ?? null,
				basePay: paySettings.base_pay ?? 0,
				feWorkOrderAmount: paySettings.fe_work_order_amount ?? 4,
				commission: paySettings.commission ?? DEFAULT_COMMISSION,
				travelCount: 0,
				estimatedDays: 0,
				bonus: false,
				feedbackCount: 0,
				estimatedDaysManual: false,
				bonusManual: false,
				travelCountManual: false,
				summary: null,
				data: [],
			},
			async () => {
				if (value) {
					await this.loadWeekData();
				}
			}
		);
	};

	changeWeek = async (newFilterDate) => {
		const sunday = moment(newFilterDate).day(0).format("YYYY-MM-DD");
		await this.setState({
			filterDate: sunday,
			estimatedDaysManual: false,
			bonusManual: false,
			travelCountManual: false,
		});
		if (this.state.field_engineer) {
			await this.loadWeekData();
		}
	};

	handleWeekDateChange = async (name, date) => {
		this.setState({showWeekPicker: false});
		await this.changeWeek(moment(date).format("YYYY-MM-DD"));
	};

	showWeekPicker = () => {
		this.setState({showWeekPicker: true});
	};

	closeWeekPicker = () => {
		this.setState({showWeekPicker: false});
	};

	renderWeekPickerModal() {
		return (
			<Modal
				isOpen={this.state.showWeekPicker}
				toggle={this.closeWeekPicker}
				size="sm"
				backdrop={true}
				style={{display: "none"}}
			>
				<ModalBody>
					<Framework.Elements.Date
						label="Select Week"
						value={this.state.filterDate}
						update={this.handleWeekDateChange}
						open={this.state.showWeekPicker}
						clearable={false}
						cancelLabel=""
					/>
				</ModalBody>
			</Modal>
		);
	}

	handleTravelCountChange = (event) => {
		const value = event.target.value;
		if (value === "" || /^\d*$/.test(value)) {
			this.setState({
				travelCount: value === "" ? 0 : parseInt(value, 10),
				travelCountManual: true,
			});
		}
	};

	handleEstimatedDaysChange = (event) => {
		const value = event.target.value;
		if (value === "" || /^-?\d*\.?\d*$/.test(value)) {
			this.setState({
				estimatedDays: value === "" ? 0 : value,
				estimatedDaysManual: true,
			});
		}
	};

	handleBonusChange = (event) => {
		this.setState({
			bonus: event.target.checked,
			bonusManual: true,
		});
	};

	selectFormula = (event) => {
		event.target.select();
	};

	openFinancialModal = (ticket) => {
		const financial = getTicketFinancial(ticket);
		if (!financial?.pkey) return;
		this.setState({
			selectedTicketPkey: ticket.pkey,
			selectedFinancialPkey: financial.pkey,
			showFinancialModal: true,
		});
	};

	closeFinancialModal = () => {
		this.setState(
			{
				showFinancialModal: false,
				selectedTicketPkey: null,
				selectedFinancialPkey: null,
			},
			() => this.loadWeekData()
		);
	};

	toggleFinancialApproved = async (ticket, financial) => {
		if (!financial?.pkey) return;
		const ticketId = financial.ticket ?? ticket?.pkey;
		if (!ticketId) return;

		const model = new API.BuildClass(Types.Tickets_Financials._name, "POST");
		model.parent = ticketId;
		model.object = {
			...financial,
			ticket: ticketId,
			parent: ticketId,
			approved: !financial.approved,
		};
		await model.submit(null, "POST");
		await this.loadWeekData();
	};

	renderFinancialModal() {
		const {showFinancialModal, selectedTicketPkey, selectedFinancialPkey} = this.state;
		return (
			<Framework.Elements.UniversalModal
				size="md"
				parent={selectedTicketPkey}
				id={selectedFinancialPkey}
				history={this.props.history}
				object={Objects.Financial}
				title="Edit Financial"
				showModal={showFinancialModal}
				reportClose={this.closeFinancialModal}
				afterSave={this.closeFinancialModal}
				hasDelete={false}
				hasSubmit={false}
				hasCancel={false}
				hideGoTo={true}
				objectProps={{
					hideBack: true,
					baseUrl: FE_WEEKLY_AUDIT_BASE_URL,
					reloadPage: this.closeFinancialModal,
					closeModal: this.closeFinancialModal,
				}}
			/>
		);
	}

	renderSummaryPanel() {
		const {
			field_engineer,
			summary,
			basePay,
			commission,
			estimatedDays,
			travelCount,
			feedbackCount,
			bonus,
			loading,
		} = this.state;

		if (!field_engineer || loading) return null;

		const weeklySummary = summary ?? calculateWeeklySummary(this.state.data ?? []);
		const formula = buildPayFormula({
			basePay: parseFloat(basePay) || 0,
			estimatedDays: parseFloat(estimatedDays) || 0,
			nonInvoicedTotal: weeklySummary.nonInvoicedTotal,
			commission: parseFloat(commission) || DEFAULT_COMMISSION,
			travelCount: parseInt(travelCount, 10) || 0,
			feedbackCount: parseInt(feedbackCount, 10) || 0,
			bonus,
		});

		return (
			<div className="fe-weekly-audit-summary" style={{marginTop: 24}}>
				<h4>Weekly Summary</h4>
				<div className="row" style={{marginBottom: 16}}>
					<div className="col-md-4">
						<strong>Non-invoiced total:</strong> $
						{weeklySummary.nonInvoicedTotal.toFixed(2)}
					</div>
					<div className="col-md-4">
						<strong>Billed work orders:</strong> {weeklySummary.billedCount}
					</div>
					<div className="col-md-4">
						<strong>Average billed WO:</strong>{" "}
						{weeklySummary.billedCount > 0
							? `$${weeklySummary.avgBilled.toFixed(2)}`
							: "-"}
					</div>
				</div>
				<div className="row" style={{marginBottom: 16}}>
					<div className="col-md-3">
						<TextField
							label="Fuel credits (travel fees)"
							type="number"
							value={travelCount}
							onChange={this.handleTravelCountChange}
							fullWidth
							variant="outlined"
							size="small"
							inputProps={{min: 0}}
						/>
					</div>
					<div className="col-md-3">
						<strong>Feedback count:</strong> {feedbackCount}
					</div>
					<div className="col-md-3">
						<TextField
							label="Estimated days worked"
							value={estimatedDays}
							onChange={this.handleEstimatedDaysChange}
							fullWidth
							variant="outlined"
							size="small"
						/>
					</div>
					<div className="col-md-3" style={{display: "flex", alignItems: "center"}}>
						<Checkbox
							checked={!!bonus}
							onChange={this.handleBonusChange}
							color="primary"
						/>
						<span>10% bonus (base pay + commission)</span>
					</div>
				</div>
				<div className="row">
					<div className="col-md-12">
						<TextField
							label="Pay formula (click to select all)"
							value={formula}
							onClick={this.selectFormula}
							fullWidth
							variant="outlined"
							size="small"
							InputProps={{readOnly: true}}
						/>
					</div>
				</div>
			</div>
		);
	}

	render() {
		const {data, loading, field_engineer, filterDate} = this.state;

		const startDate = filterDate ? moment(filterDate) : moment().day(0);
		const endDate = filterDate
			? moment(filterDate).add(6, "days")
			: moment().day(0).add(6, "days");

		return (
			<Card>
				<CardContent>
					<div className="table-bar">
						<div className="row">
							<div className={'col-3'} style={{paddingLeft: '15px', paddingRight: '15px'}}>
								<label style={{display: "block", fontSize: 12, color: "rgba(0, 0, 0, 0.54)", marginBottom: 4}}>
									Field Engineer
								</label>
								<Select
									placeholder="Field Engineer"
									isClearable={true}
									value={this.getSelectedFieldEngineerOption()}
									onChange={this.handleFieldEngineerChange}
									options={this.state.fieldEngineerOptions}
									menuPortalTarget={document.body}
									menuPosition="fixed"
									styles={{
										menuPortal: (base) => ({...base, zIndex: 9999}),
									}}
								/>
							</div>
							<div className={'col-3'}>
								<div className="rbc-toolbar">
								<IconButton
									onClick={() =>
										this.changeWeek(
											moment(filterDate).subtract(7, "days").day(0).format("YYYY-MM-DD")
										)
									}
								>
									<LeftArrowIcon />
								</IconButton>
								<button
									className="rbc-today"
									type="button"
									style={{width: "auto"}}
									onClick={() => this.changeWeek(moment().day(0).format("YYYY-MM-DD"))}
								>
									This Week
								</button>
								<IconButton
									onClick={() =>
										this.changeWeek(
											moment(filterDate).add(7, "days").day(0).format("YYYY-MM-DD")
										)
									}
								>
									<RightArrowIcon />
								</IconButton>
								<label
									className="fe-weekly-audit-week-label"
									onClick={this.showWeekPicker}
									title="Click to pick a week"
								>
									{startDate.format("MMMM D")} - {endDate.format("MMMM D")}
								</label>
							</div>
							</div>
						{field_engineer && (
							<div className={'col-6'}>
								<div className='d-flex' style={{marginRight: '15px'}}>
									{this.renderHeaderRight("FE_Weekly_Audit", this.columns)}
								</div>
							</div>
						)}
						</div>

					</div>

					{!field_engineer && (
						<p style={{padding: "24px 0", color: "#666"}}>
							Select a field engineer to view work orders.
						</p>
					)}

					{field_engineer && (
						<>
							<ReactTable
								columns={this.columns}
								data={data}
								loading={loading}
								loadingText="Requesting Data"
								showPagination={false}
								defaultPageSize={data.length || 1}
								pageSize={data.length || 1}
								className="-striped -highlight fe-weekly-audit-table"
								minRows={0}
							/>
							{!loading && data.length === 0 && (
								<p style={{padding: "12px 0", color: "#666"}}>
									No work orders for this week.
								</p>
							)}
							{this.renderSummaryPanel()}
						</>
					)}

					{this.renderFinancialModal()}
					{this.renderWeekPickerModal()}
					<Framework.Elements.LoadingSpinner isLoading={loading} />
				</CardContent>
			</Card>
		);
	}
}
